import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';

export interface SearchOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface SearchSelectProps {
  placeholder: string;
  /** Async lookup, called with the debounced search term */
  fetchOptions: (term: string) => Promise<SearchOption[]>;
  selected: SearchOption | null;
  onSelect: (option: SearchOption) => void;
  onClear: () => void;
  ariaLabel?: string;
  /** Search text the picker opens with (e.g. a stored name to match against). */
  initialTerm?: string;
}

/**
 * Debounced async search picker for the Accounts tool (pick a user, a
 * workspace, …). Shows the selection as a clearable chip.
 */
const SearchSelect: React.FC<SearchSelectProps> = ({
  placeholder,
  fetchOptions,
  selected,
  onSelect,
  onClear,
  ariaLabel,
  initialTerm,
}) => {
  const [term, setTerm] = useState(initialTerm ?? '');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!term.trim()) {
      setOptions([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await fetchOptions(term.trim());
        if (!cancelled) {
          setOptions(results);
          setFailed(false);
          setOpen(true);
        }
      } catch {
        // A failed lookup is not an empty result. Saying "No matches" here hid a
        // shadowed backend route for six weeks (PRD v2 §5.14) — show the failure.
        if (!cancelled) {
          setOptions([]);
          setFailed(true);
          setOpen(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // fetchOptions is expected to be stable (module scope or useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (selected) {
    return (
      <div className="flex h-9 items-center gap-2 rounded-lg border border-border-light bg-background-secondary px-3 text-sm dark:border-border-dark dark:bg-background-dark-tertiary">
        <span className="min-w-0 truncate font-medium text-text-primary dark:text-text-dark-primary">
          {selected.label}
        </span>
        {selected.sublabel && (
          <span className="hidden truncate text-xs text-text-tertiary dark:text-text-dark-tertiary sm:inline">
            {selected.sublabel}
          </span>
        )}
        <button
          onClick={onClear}
          aria-label="Clear selection"
          className="ml-auto shrink-0 rounded p-0.5 text-text-tertiary hover:text-text-primary dark:text-text-dark-tertiary dark:hover:text-text-dark-primary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary dark:text-text-dark-tertiary" />
        <input
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setOpen(false);
          }}
          placeholder={placeholder}
          aria-label={ariaLabel ?? placeholder}
          className="h-9 w-full rounded-lg border border-border-light bg-white pl-8 pr-8 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
        />
        {loading && (
          <Loader2 className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-tertiary dark:text-text-dark-tertiary" />
        )}
      </div>
      {open && (
        <ul
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-lg border border-border-light bg-white py-1 shadow-lg dark:border-border-dark dark:bg-background-dark-secondary"
        >
          {options.length === 0 ? (
            <li className="px-3 py-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
              {loading ? 'Searching…' : failed ? 'Search failed — try again' : 'No matches'}
            </li>
          ) : (
            options.map((option) => (
              <li key={option.id} role="option" aria-selected={false}>
                <button
                  onClick={() => {
                    onSelect(option);
                    setTerm('');
                    setOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary"
                >
                  <span className="block font-medium text-text-primary dark:text-text-dark-primary">
                    {option.label}
                  </span>
                  {option.sublabel && (
                    <span className="block text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {option.sublabel}
                    </span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchSelect;
